import { useRef } from 'react';
import { VALUE_PHILOSOPHY_OPTIONS, type ValuePhilosophy } from '@mg/shared';
import { Button } from '../../../components/ui/Button';
import { PriceTier } from './PriceTier';

/**
 * Value philosophy: one of four option cards (spec:242-251), a radio group with roving focus (the
 * pattern of AppearanceCard): Tab enters the group once, at the chosen card (or the first), and the
 * arrow keys move the choice and the focus, wrapping. Each card has its name and description on the
 * left and its price tier at the right edge. "Clear choice" takes the value back to none.
 *
 * Label it with the field shell's group label: `<BrandFieldShell group id={id}>` and
 * `labelId={fieldPartIds(id).label}`.
 */
export function ValuePhilosophyField({
  id,
  labelId,
  value,
  onChange,
  readOnly = false,
  disabled = false,
  describedBy,
}: {
  id: string;
  labelId: string;
  value: ValuePhilosophy | null;
  onChange: (next: ValuePhilosophy | null) => void;
  readOnly?: boolean;
  disabled?: boolean;
  describedBy?: string;
}) {
  const cards = useRef(new Map<ValuePhilosophy, HTMLButtonElement>());
  const inert = readOnly || disabled;
  const chosenAt = VALUE_PHILOSOPHY_OPTIONS.findIndex((option) => option.value === value);
  // The one card Tab stops at: the chosen one, or the first while none is chosen.
  const tabStop = chosenAt >= 0 ? chosenAt : 0;

  const move = (from: number, delta: number) => {
    const count = VALUE_PHILOSOPHY_OPTIONS.length;
    const next = VALUE_PHILOSOPHY_OPTIONS[(from + delta + count) % count];
    onChange(next.value);
    cards.current.get(next.value)?.focus();
  };

  return (
    <div>
      <div
        id={id}
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        aria-readonly={readOnly || undefined}
        className="grid gap-3 md:grid-cols-2"
      >
        {VALUE_PHILOSOPHY_OPTIONS.map((option, index) => {
          const checked = option.value === value;
          return (
            <button
              key={option.value}
              ref={(el) => {
                if (el) cards.current.set(option.value, el);
                else cards.current.delete(option.value);
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={index === tabStop ? 0 : -1}
              disabled={inert}
              onClick={() => onChange(option.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                  e.preventDefault();
                  move(index, 1);
                } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                  e.preventDefault();
                  move(index, -1);
                }
              }}
              // Chosen: a 2px action border and the tint fill; the padding drops by the extra pixel of
              // border so the content does not shift (AppearanceCard).
              className="focus-ring flex items-start justify-between gap-3 rounded-xl border border-line bg-card p-4 text-left disabled:cursor-not-allowed aria-checked:border-2 aria-checked:border-action aria-checked:bg-tint aria-checked:p-[calc(1rem-1px)]"
            >
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-ink">{option.label}</span>
                <span className="mt-0.5 block text-sm text-ink-muted">{option.description}</span>
              </span>
              <PriceTier tier={option.tier} />
            </button>
          );
        })}
      </div>
      {value !== null && !inert && (
        <Button type="button" variant="plain" size="sm" className="mt-2" onClick={() => onChange(null)}>
          Clear choice
        </Button>
      )}
    </div>
  );
}
